import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../services/auth/auth.service';
import {RouteService} from '../../services/route/route.service';
import {UtilService} from '../../services/util/util.service';
import {ViewService} from '../../services/view/view.service';
import {cloneDeep} from 'lodash-es';
import {interval, Subscription, tap} from 'rxjs';
import {StorageService} from '../../services/storage/storage.service';
import {RegistrationUserFormComponent} from '../registration/user/form/registration-user-form.component';
import moment from 'moment';
import {CartService} from '../../services/cart/cart.service';
import {Router} from "@angular/router";
import {set} from "d3-collection";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public email: string;
  public password: string;
  public remember: boolean;
  public isLoading = false;
  public isAuthLoading = false;
  public isIE = false;
  public salespointRoleSelection = false;
  public timeoutUntil: number;
  public timeoutEmail: string;
  public timeoutLeft: string;
  public timeoutActive: boolean;
  private _subscriptions: Subscription[] = [];

  constructor(
    private authService: AuthService,
    private routeService: RouteService,
    public viewService: ViewService,
    public utilService: UtilService,
    private storageService: StorageService,
    public router: Router,
    private cartService: CartService) {}

  public ngOnInit(): void {
    this.isAuthLoading = true;
    this._subscriptions.push(this.authService.checkStorage().pipe(
      tap({error: () => this.isAuthLoading = false})
    ).subscribe((loggedIn) => {
      if (loggedIn) {
        this.authService.navigateToBaseUrl().then(_ => {
          this.isAuthLoading = false;
        });
      } else {
        this.isAuthLoading = false;
      }
    }));
    this.initTimer();
    this._subscriptions.push(this.storageService.changes.subscribe(({key, value}) => {
      if (key === AuthService.STORAGE_AUTH_USER_LOGGED_IN && value) {
        setTimeout(() => {
          if (!this.utilService.userLoggedInNoRedirect) {
            this.routeService.navigate('/');
          }
        }, 1000);
      }
    }));
    this._subscriptions.push(this.utilService.userPermissionsFetched.subscribe((res) => {
      if (res) {
        if (this.authService.primarySalespoints.length > 1 || this.authService.aliasSalespoints.length > 1) {
          const urlTree = this.router.parseUrl(this.authService.redirectUrl || '');
          const path = urlTree?.root?.children?.primary?.segments?.map(s => s.path) || [];
          if (path?.join('/') === 'login/oauth') {
            this.routeService.navigateWithQueryParams(this.authService.redirectUrl).then(() => {
              this.authService.redirectUrl = '';
            });
          } else {
            this.utilService.userLoggedInNoRedirect = true;
            this.salespointRoleSelection = true;
          }
        } else {
          const redirect = this.authService.redirectUrl || this.authService.baseUrl;
          this.routeService.navigateWithQueryParams(redirect).then(() => {
            this.authService.redirectUrl = '';
          });
        }
        this.isLoading = false;
      }
    }));
    const cachedTimeout = this.storageService.get(AuthService.STORAGE_AUTH_USER_LOGIN_TIMEOUT)
    if (cachedTimeout) {
      const json = JSON.parse(cachedTimeout);
      if (json) {
        this.timeoutUntil = json.until;
        this.timeoutEmail = json.email;
      }
    }
    const credentials = this.authService.getCredentials();
    if (credentials) {
      this.email = credentials;
      this.remember = true;
    }
    this.isIE = UtilService.getBrowser('ie');
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  public login(): void {
    this.isLoading = true;
    this.utilService.userLoggedInNoRedirect = true;
    this.authService.loginObs(this.email, this.password, this.remember).subscribe({
      next: (value) => {
        if (!value) {
          this.isLoading = false;
          return;
        }
        delete this.timeoutUntil;
        delete this.timeoutEmail;
        this.storageService.remove(AuthService.STORAGE_AUTH_USER_LOGIN_TIMEOUT);
      },
      error: (e) => {
        if (e.error?.message === 'api_users_user_is_unverified') {
          this.storageService.set(RegistrationUserFormComponent.STORAGE_USER_REGISTRATION_FORM, JSON.stringify({email: this.email}));
          this.routeService.navigate('/registration/user/resend');
        }
        if (e.error?.message === 'api_users_user_is_locked') {
          this.timeoutUntil = e.error.timeoutUntil;
          this.timeoutEmail = cloneDeep(this.email);
          this.storageService.set(AuthService.STORAGE_AUTH_USER_LOGIN_TIMEOUT, JSON.stringify({ email: this.timeoutEmail, until: this.timeoutUntil }));
        }
        this.isLoading = false;
      }
    });
  }

  get hasTimeout(): boolean {
    return this.timeoutUntil && this.timeoutEmail === this.email;
  }

  initTimer(): void {
    const timerSub = interval(1000).subscribe(() => {
      if (this.hasTimeout) {
        const seconds = moment().diff(this.timeoutUntil, 'second') * -1;
        if (seconds > 0) {
          this.timeoutActive = true;
          const format = seconds < 3600 ? 'mm:ss' : 'hh:mm:ss'; // no hour to display if under 3600
          this.timeoutLeft = moment().startOf('day').seconds(seconds).format(format);
        } else {
          this.timeoutActive = false;
        }
      }
    });
    this._subscriptions.push(timerSub);
  }

  public logout(): void {
    this.authService.logout();
    this.salespointRoleSelection = false;
  }
}
